import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { formatCurrency } from '@/lib/utils';

interface ConsignmentItem {
  id: string;
  productId: string;
  quantitySent: number | string;
  quantitySold: number | string;
  quantityReturned: number | string;
  sellingPrice: number | string;
  currency: string;
  product: {
    name: string;
    model: {
      name: string;
      brand: {
        name: string;
      };
    };
  };
}

interface Consignment {
  id: string;
  items: ConsignmentItem[];
}

interface SaleItem {
  consignmentItemId: string;
  quantity: number;
  unitPrice: number;
}

interface RecordSaleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  consignment: Consignment;
  onSuccess: () => void;
}

export function RecordSaleModal({
  open,
  onOpenChange,
  consignment,
  onSuccess,
}: RecordSaleModalProps) {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');

  const [saleDate, setSaleDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [saleItems, setSaleItems] = useState<Record<string, SaleItem>>({});

  // Filter items that have available quantity
  const availableItems = consignment.items.filter((item) => {
    const available =
      Number(item.quantitySent) - Number(item.quantitySold) - Number(item.quantityReturned);
    return available > 0;
  });

  const toggleItem = (item: ConsignmentItem, checked: boolean) => {
    if (checked) {
      setSaleItems((prev) => ({
        ...prev,
        [item.id]: {
          consignmentItemId: item.id,
          quantity: 1,
          unitPrice: Number(item.sellingPrice),
        },
      }));
    } else {
      setSaleItems((prev) => {
        const newItems = { ...prev };
        delete newItems[item.id];
        return newItems;
      });
    }
  };

  const updateItemQuantity = (itemId: string, quantity: number) => {
    setSaleItems((prev) => {
      const existing = prev[itemId];
      if (!existing) return prev;
      return {
        ...prev,
        [itemId]: {
          ...existing,
          quantity,
        },
      };
    });
  };

  const updateItemPrice = (itemId: string, unitPrice: number) => {
    setSaleItems((prev) => {
      const existing = prev[itemId];
      if (!existing) return prev;
      return {
        ...prev,
        [itemId]: {
          ...existing,
          unitPrice,
        },
      };
    });
  };

  const recordSaleMutation = trpc.tradingConsignment.recordSale.useMutation({
    onSuccess: () => {
      showSuccessToast(tCommon('created'));
      onSuccess();
      // Reset form
      setSaleItems({});
      setNotes('');
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const handleSubmit = () => {
    const items = Object.values(saleItems).filter((item) => item.quantity > 0);
    if (items.length === 0) return;

    recordSaleMutation.mutate({
      consignmentId: consignment.id,
      saleDate: new Date(saleDate),
      notes: notes || undefined,
      items,
    });
  };

  const selectedItems = Object.values(saleItems);
  // Sale prices are denominated per consignment item — keep the total per
  // currency instead of summing across currencies under items[0]'s label.
  const currencyByItemId = new Map(
    consignment.items.map((item) => [item.id, item.currency || 'EUR'])
  );
  const totalsByCurrency = selectedItems.reduce((totals, item) => {
    const ccy = currencyByItemId.get(item.consignmentItemId) ?? 'EUR';
    totals.set(ccy, (totals.get(ccy) ?? 0) + item.quantity * item.unitPrice);
    return totals;
  }, new Map<string, number>());
  const totalLabel = [...totalsByCurrency.entries()]
    .map(([ccy, value]) => formatCurrency(value, ccy))
    .join(' + ');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('consignment.sale.title')}</DialogTitle>
          <DialogDescription>{t('consignment.sale.selectItems')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>{t('consignment.sale.saleDate')}</Label>
            <Input
              type="date"
              value={saleDate}
              onChange={(e) => setSaleDate(e.target.value)}
            />
          </div>

          {availableItems.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">
              {t('consignment.sale.noItemsToSell')}
            </p>
          ) : (
            <div className="border rounded-lg divide-y">
              {availableItems.map((item) => {
                const available =
                  Number(item.quantitySent) - Number(item.quantitySold) - Number(item.quantityReturned);
                const isSelected = !!saleItems[item.id];
                const saleItem = saleItems[item.id];

                return (
                  <div key={item.id} className="p-4">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => toggleItem(item, !!checked)}
                      />
                      <div className="flex-1">
                        <div className="font-medium">{item.product.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {item.product.model.brand.name} - {item.product.model.name}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {t('consignment.sale.available')}: {available} |{' '}
                          {t('consignment.form.sellingPrice')}: {formatCurrency(Number(item.sellingPrice), item.currency)}
                        </div>
                      </div>
                    </div>

                    {isSelected && (
                      <div className="mt-3 ml-7 grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <Label className="text-xs">{t('consignment.sale.quantitySold')}</Label>
                          <Input
                            type="number"
                            min={1}
                            max={available}
                            value={saleItem?.quantity || 1}
                            onChange={(e) => updateItemQuantity(item.id, parseInt(e.target.value) || 1)}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">{t('consignment.sale.unitPrice')}</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min={0}
                            value={saleItem?.unitPrice || Number(item.sellingPrice)}
                            onChange={(e) => updateItemPrice(item.id, parseFloat(e.target.value) || 0)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="space-y-2">
            <Label>{t('consignment.form.notes')}</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>

          {selectedItems.length > 0 && (
            <div className="rounded-lg bg-muted p-4">
              <div className="flex justify-between items-center text-lg font-medium">
                <span>{tCommon('total')}:</span>
                <span>{totalLabel || '-'}</span>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={recordSaleMutation.isPending}>
            {tCommon('cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedItems.length === 0 || recordSaleMutation.isPending}
          >
            {recordSaleMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('consignment.detail.recordSale')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
